/**
 * 랜딩 첫 화면의 「개발 중 · 정식 서비스 아님」 팝업.
 *
 * 공개 주소로 들어온 사람이 이것을 완성된 서비스로 오해하지 않게 한다: 규칙·화면·저장이
 * 바뀌거나 지워질 수 있고, 결제·가입·개인정보 수집이 없다는 것.
 * 네이티브 <dialog> 의 showModal 을 쓴다 — 초점 가두기와 Esc 닫기를 브라우저가 맡는다.
 */

import { useEffect, useRef, useState } from 'react';
import {
  DEV_NOTICE_ETA,
  etaEn,
  etaKo,
  localDay,
  readHiddenOn,
  shouldShowDevNotice,
  writeHiddenOn,
} from './devNoticeRule.js';

export function DevNotice(): JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null);
  const okRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(() => shouldShowDevNotice(readHiddenOn(), localDay(new Date())));
  const [hideToday, setHideToday] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!open || !dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    // showModal 은 첫 초점 요소(메일 링크)로 간다 — 엔터 한 번에 닫히도록 확인 단추로 옮긴다.
    okRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const close = (): void => {
    if (hideToday) writeHiddenOn(localDay(new Date()));
    ref.current?.close();
    setOpen(false);
  };

  return (
    <dialog
      ref={ref}
      className="devnotice"
      aria-labelledby="devnotice-title"
      aria-describedby="devnotice-body"
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
    >
      <span className="devnotice__badge">PROTOTYPE</span>
      <h2 id="devnotice-title" className="devnotice__title">
        개발 중 · 정식 서비스 아님
      </h2>
      <div id="devnotice-body" className="devnotice__body">
        <p>지금 보시는 것은 THE LAST ROLL 의 <strong>시험 개발 중</strong>인 시제품입니다.</p>
        <p className="devnotice__eta">
          <span className="devnotice__eta-label">예상 개발 완료일</span>
          <time dateTime={DEV_NOTICE_ETA}>{etaKo(DEV_NOTICE_ETA)}</time>
        </p>
        <ul>
          <li>규칙과 화면이 예고 없이 바뀝니다.</li>
          <li>저장한 모험이 업데이트 때 지워질 수 있습니다.</li>
          <li>결제 · 회원가입 · 개인정보 수집이 없습니다. 저장은 이 브라우저에만 남습니다.</li>
        </ul>
        <p className="devnotice__en" lang="en">
          In development — not an official service. Expected completion: {etaEn(DEV_NOTICE_ETA)}. Rules, screens and saves may change or be reset.
        </p>
        <p className="devnotice__contact">
          문의 <a href="mailto:projectteamforyou@gmail.com">projectteamforyou@gmail.com</a>
        </p>
      </div>
      <label className="devnotice__today">
        <input type="checkbox" checked={hideToday} onChange={(e) => setHideToday(e.target.checked)} />
        오늘 하루 보지 않기
      </label>
      <button ref={okRef} type="button" className="roll-btn devnotice__ok" onClick={close}>
        확인했습니다
      </button>
    </dialog>
  );
}
